import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config/apiConfig';
import {
  XMarkIcon,
  MapPinIcon,
  SparklesIcon,
  TrashIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Tour, Guide } from '../../types';
import { guideService } from '../../services/guideService';
import { categoryService } from '../../services/categoryService';
import { accommodationService } from '../../services/accommodationService';
import { apiService } from '../../services/apiService';
import { AccommodationProvider } from '../../types/accommodation';
import TierEditor from './TierEditor';
import { useToast } from '../../contexts/ToastContext';

interface TourFormProps {
  tour?: Tour | null;
  onSave: (tourData: Omit<Tour, 'id'>) => Promise<void> | void;
  onCancel: () => void;
}

const TourForm: React.FC<TourFormProps> = ({ tour, onSave, onCancel }) => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  // Keep full Category objects so we can resolve categoryId on change
  const [categoryObjects, setCategoryObjects] = useState<{ id: string; name: string }[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [accommodations, setAccommodations] = useState<AccommodationProvider[]>([]);
  const [loadingAccommodations, setLoadingAccommodations] = useState(true);
  const { showError } = useToast();

  const [formData, setFormData] = useState<Omit<Tour, 'id'>>({
    title: '',
    description: '',
    shortDescription: '',
    duration: '',
    price: 0,
    currency: 'USD',
    difficulty: 'Moderate',
    category: '',
    // categoryId is resolved when the user picks a category
    images: [],
    highlights: [],
    itinerary: [],
    includes: [],
    excludes: [],
    requirements: [],
    maxParticipants: 8,
    minParticipants: 1,
    rating: 4.5,
    reviewCount: 0,
    location: {
      name: '',
      country: '',
      region: '',
      coordinates: { lat: 0, lng: 0 },
      address: ''
    },
    guides: [],
    availability: [],
    active: true, // Legacy
    status: 'active',
    bookingOpenDate: '',
    bookingCloseDate: '',
    safariStartDate: '',
    safariEndDate: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tierVariants: {},
    discountedTierVariants: undefined,
    seasonalAdjustments: [],
    extras: [],
    depositPercentage: 30, // Default 30%
    allowInstallments: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newImage, setNewImage] = useState('');
  const [guideSearchTerm, setGuideSearchTerm] = useState('');
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValidImageUrl = (url: string): boolean => {
    if (!url || !url.trim()) return false;
    const trimmedUrl = url.trim();
    if (trimmedUrl.startsWith('data:image/')) {
      const base64Pattern = /^data:image\/(jpeg|jpg|png|gif|webp|svg\+xml|bmp|tiff);base64,[A-Za-z0-9+/=]+$/i;
      return base64Pattern.test(trimmedUrl);
    }
    try {
      const urlObj = new URL(trimmedUrl);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const loadGuides = async () => {
      try {
        setLoadingGuides(true);
        const activeGuides = await guideService.getActiveGuides();
        setGuides(activeGuides);
      } catch (error) {
        console.error('Error loading guides:', error);
      } finally {
        setLoadingGuides(false);
      }
    };
    loadGuides();

    const loadAccommodations = async () => {
      try {
        setLoadingAccommodations(true);
        const activeAccommodations = await accommodationService.getAllProviders(true);
        setAccommodations(activeAccommodations);
      } catch (error) {
        console.error('Error loading accommodations:', error);
      } finally {
        setLoadingAccommodations(false);
      }
    };
    loadAccommodations();

    // Load from localStorage if not editing an existing tour
    if (!tour) {
      const cachedData = localStorage.getItem('tour_form_cache');
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          setFormData(prev => ({ ...prev, ...parsed }));
          console.log('📦 TourForm: Recovered unsaved data from cache');
        } catch (e) {
          console.error('Error parsing tour form cache:', e);
        }
      }
    }
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const [activeCats, user] = await Promise.all([
          categoryService.getActiveCategories(),
          apiService.getCurrentUser()
        ]);
        
        let allowedCats = activeCats;
        if (user && user.email !== 'sighttrailsafaris@gmail.com' && user.admin_permissions) {
          const hasToursPerm = user.admin_permissions.includes('tours');
          const hasAllToursPerm = user.admin_permissions.includes('tours:all');
          const specificCategoryPerms = user.admin_permissions
            .filter((p: string) => p.startsWith('tours:') && p !== 'tours:all')
            .map((p: string) => p.split(':')[1]);

          if (hasToursPerm && !hasAllToursPerm && specificCategoryPerms.length > 0) {
            allowedCats = activeCats.filter(cat => specificCategoryPerms.includes(cat.id));
          }
        }
        
        setCategoryObjects(allowedCats.map(c => ({ id: c.id, name: c.name })));
        setCategories(allowedCats.map(c => c.name));
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories(['Safari', 'Cultural', 'Adventure', 'Photography']);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // When categories load, set default category + categoryId for new tours
  useEffect(() => {
    if (categoryObjects.length > 0 && !tour && !formData.category) {
      const first = categoryObjects[0];
      setFormData(prev => ({ ...prev, category: first.name, categoryId: first.id }));
    }
  }, [categoryObjects, tour, formData.category]);

  useEffect(() => {
    if (tour) {
      const safeArray = (val: any): any[] => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string' && val.trim()) {
          if (val.startsWith('[') && val.endsWith(']')) {
            try { return JSON.parse(val); } catch (e) { return [val]; }
          }
          return val.split(',').map(s => s.trim()).filter(Boolean);
        }
        return [];
      };

      setFormData({
        title: tour.title || '',
        description: tour.description || '',
        shortDescription: tour.shortDescription || '',
        duration: tour.duration || '',
        price: tour.price || 0,
        currency: tour.currency || 'USD',
        difficulty: tour.difficulty || 'Moderate',
        category: tour.category || 'Safari',
        categoryId: (tour as any).categoryId || '',
        images: safeArray(tour.images),
        highlights: safeArray(tour.highlights),
        itinerary: (tour.itinerary || []).map((day: any) => ({
          ...day,
          activities: safeArray(day.activities),
          meals: safeArray(day.meals)
        })),
        includes: safeArray(tour.includes),
        excludes: safeArray(tour.excludes),
        requirements: safeArray(tour.requirements),
        maxParticipants: tour.maxParticipants || 8,
        minParticipants: tour.minParticipants || 1,
        rating: tour.rating || 4.5,
        reviewCount: tour.reviewCount || 0,
        location: {
          name: tour.location?.name || '',
          country: tour.location?.country || '',
          region: tour.location?.region || '',
          coordinates: tour.location?.coordinates || { lat: 0, lng: 0 },
          address: tour.location?.address || ''
        },
        guides: tour.guides || [],
        availability: tour.availability || [],
        active: tour.active !== undefined ? tour.active : true, // Legacy
        status: tour.status || 'active',
        bookingOpenDate: tour.bookingOpenDate || '',
        bookingCloseDate: tour.bookingCloseDate || '',
        safariStartDate: tour.safariStartDate || '',
        safariEndDate: tour.safariEndDate || '',
        createdAt: tour.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tierVariants: tour.tierVariants || {},
        discountedTierVariants: tour.discountedTierVariants,
        seasonalAdjustments: tour.seasonalAdjustments || [],
        extras: tour.extras || [],
        depositPercentage: tour.depositPercentage !== undefined ? tour.depositPercentage : 30,
        allowInstallments: tour.allowInstallments !== undefined ? tour.allowInstallments : true
      });
    }
  }, [tour]);

  // Persist form data to localStorage with safety guard
  useEffect(() => {
    if (!tour && formData.title && formData.title.length > 2) {
      localStorage.setItem('tour_form_cache', JSON.stringify(formData));
    }
  }, [formData, tour]);

  const clearFormCache = () => {
    localStorage.removeItem('tour_form_cache');
  };

  const difficulties = ['Easy', 'Moderate', 'Challenging'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (name === 'category') {
      // Resolve the matching categoryId and update both fields atomically
      const matched = categoryObjects.find(c => c.name === value);
      setFormData(prev => ({
        ...prev,
        category: value,
        categoryId: matched ? matched.id : (prev as any).categoryId || ''
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (Number(value) || 0) : value
    }));
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, [name]: value }
    }));
  };

  const handleAddImage = () => {
    if (newImage.trim() && isValidImageUrl(newImage)) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage.trim()]
      }));
      setNewImage('');
      setImageErrors({});
    } else {
      showError('Invalid Image', 'Please enter a valid image URL or base64 data URL.');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataPayload = new FormData();
    formDataPayload.append('image', file);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formDataPayload,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      if (response.ok && data.success && data.url) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, data.url]
        }));
      } else {
        throw new Error(data.message || 'Image upload failed');
      }
    } catch (err: any) {
      showError('Upload Error', err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.location.name.trim()) newErrors.location = 'Location is required';
    if (formData.images.length === 0) newErrors.images = 'At least one image is required';

    const hasTiers = formData.tierVariants && Object.keys(formData.tierVariants).length > 0;
    if (!hasTiers) {
      newErrors.tierVariants = 'At least one pricing tier (Budget, Standard, or Luxury) must be configured';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      console.log('🚀 TourForm: Submitting valid formData:', formData);
      setIsSaving(true);
      try {
        await onSave(formData);
        clearFormCache();
        // Dispatch global events so CategoryPackages, FeaturedTours, AllCategories
        // all pick up the new categoryId immediately without a full page reload.
        const categoryId = (formData as any).categoryId;
        window.dispatchEvent(new Event('toursUpdated'));
        if (categoryId) {
          window.dispatchEvent(
            new CustomEvent('categoryToursUpdated', {
              detail: { categoryId, tours: [] }   // empty → triggers a live re-fetch
            })
          );
        }
      } catch (err) {
        setIsSaving(false);
      }
    } else {
      console.warn('❌ TourForm: Validation failed:', newErrors);
      if (newErrors.tierVariants) {
        showError('Missing Pricing', newErrors.tierVariants);
      }
    }
  };

  const handleCancel = () => {
    if (!tour && formData.title && window.confirm('Clear unsaved changes?')) {
      clearFormCache();
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 z-[100]">
      <div className="bg-white dark:bg-slate-900/95 dark:backdrop-blur-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl h-full sm:h-auto max-h-[100dvh] sm:max-h-[90vh] flex flex-col overflow-hidden border border-white/10">
        <div className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="flex items-center justify-between mb-8 border-b dark:border-white/10 pb-4">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-slate-400 bg-clip-text text-transparent">
              {tour ? 'Edit Safari Package' : 'Create New Safari'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Safari Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all dark:text-white ${errors.title ? 'border-red-500' : 'border-gray-200 dark:border-slate-700'}`}
                  placeholder="e.g. Majestic Maasai Mara Expedition"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1 font-medium">{errors.title}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={loadingCategories}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all dark:text-white disabled:opacity-50"
                >
                  {loadingCategories ? (
                    <option value="">Loading...</option>
                  ) : (
                    categories.map(c => <option key={c} value={c}>{c}</option>)
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className={`w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all dark:text-white ${errors.description ? 'border-red-500' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder="Enter tour description"
              />
              {errors.description && <p className="text-red-500 text-xs mt-1 font-medium">{errors.description}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Short Description</label>
              <input
                type="text"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all dark:text-white"
                placeholder="Enter short description"
              />
            </div>

            {/* Highlights */}
            <div className="bg-gray-50 dark:bg-slate-800/30 p-6 rounded-2xl border border-gray-100 dark:border-white/5">
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-4">Tour Highlights</label>
              <div className="space-y-3">
                {formData.highlights.map((h, i) => (
                  <div key={`highlight-${i}-${h.slice(0, 10)}`} className="flex gap-2">
                    <input
                      value={h}
                      onChange={(e) => {
                        const newH = [...formData.highlights];
                        newH[i] = e.target.value;
                        setFormData(prev => ({ ...prev, highlights: newH }));
                      }}
                      className="flex-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white"
                      placeholder="e.g. Witness the Great Migration"
                    />
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }))} className="text-red-500 hover:text-red-700 px-2 transition-colors">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, highlights: [...prev.highlights, ''] }))} className="text-sm font-bold text-emerald-600 hover:text-emerald-500 flex items-center gap-2 mt-2">
                  <span className="text-xl">+</span> Add Highlight
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Location *</label>
              <div className="flex items-center gap-2">
                <MapPinIcon className="h-5 w-5 text-emerald-500" />
                <input
                  type="text"
                  name="name"
                  value={formData.location.name}
                  onChange={handleLocationChange}
                  className={`flex-1 px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all dark:text-white ${errors.location ? 'border-red-500' : 'border-gray-200 dark:border-slate-700'}`}
                  placeholder="Enter location name"
                />
              </div>
              {errors.location && <p className="text-red-500 text-xs mt-1 font-medium">{errors.location}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white transition-all"
                  placeholder="e.g., 7 days"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Difficulty</label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white transition-all"
                >
                  {difficulties.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Min Participants</label>
                <input
                  type="number"
                  name="minParticipants"
                  value={formData.minParticipants}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Max Participants</label>
                <input
                  type="number"
                  name="maxParticipants"
                  value={formData.maxParticipants}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white transition-all"
                />
              </div>
            </div>

            {/* Lifecycle & Availability */}
            <div className="mt-12 border-t dark:border-white/10 pt-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <SparklesIcon className="h-6 w-6 text-emerald-500" />
                Lifecycle & Availability
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white transition-all"
                  >
                    <option value="active">Active (Fully Bookable)</option>
                    <option value="scheduled">Scheduled (Date-Bound)</option>
                    <option value="suspended">Suspended (Temporarily Unavailable)</option>
                    <option value="deactivated">Deactivated (Hidden from Public)</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic px-1 leading-relaxed">
                    {formData.status === 'active' && 'Always visible and bookable for valid future dates.'}
                    {formData.status === 'scheduled' && 'Package has a specific booking window and set travel dates.'}
                    {formData.status === 'suspended' && 'Visible but faded. Shows "Coming Soon". Cannot be booked.'}
                    {formData.status === 'deactivated' && 'Completely hidden from users. Only visible in Admin Dashboard.'}
                  </p>
                </div>

                {formData.status === 'scheduled' && (
                  <div className="space-y-4 bg-gray-50 dark:bg-slate-800/30 p-4 rounded-xl border border-gray-100 dark:border-white/5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Booking Opens</label>
                        <input
                          type="datetime-local"
                          name="bookingOpenDate"
                          value={formData.bookingOpenDate ? new Date(formData.bookingOpenDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, bookingOpenDate: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                          className="w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Booking Closes</label>
                        <input
                          type="datetime-local"
                          name="bookingCloseDate"
                          value={formData.bookingCloseDate ? new Date(formData.bookingCloseDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, bookingCloseDate: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                          className="w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Safari Starts</label>
                        <input
                          type="date"
                          name="safariStartDate"
                          value={formData.safariStartDate ? new Date(formData.safariStartDate).toISOString().split('T')[0] : ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, safariStartDate: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                          className="w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Safari Ends</label>
                        <input
                          type="date"
                          name="safariEndDate"
                          value={formData.safariEndDate ? new Date(formData.safariEndDate).toISOString().split('T')[0] : ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, safariEndDate: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                          className="w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Specific Availability Dates */}
              <div className="mt-8 bg-gray-50 dark:bg-slate-800/30 p-6 rounded-2xl border border-gray-100 dark:border-white/5">
                <label className="block text-sm font-bold text-gray-900 dark:text-white mb-4">Specific Tour Dates (Availability)</label>
                <div className="space-y-4">
                  {(formData.availability || []).map((avail, i) => (
                    <div key={avail.id || i} className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-700 relative group">
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500">Starts</label>
                        <input
                          type="date"
                          value={avail.startDate ? avail.startDate.split('T')[0] : ''}
                          onChange={(e) => {
                            const newAvail = [...(formData.availability || [])];
                            newAvail[i].startDate = new Date(e.target.value).toISOString();
                            setFormData(prev => ({ ...prev, availability: newAvail }));
                          }}
                          className="w-full border-none p-0 text-xs dark:text-white bg-transparent focus:ring-0"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500">Ends</label>
                        <input
                          type="date"
                          value={avail.endDate ? avail.endDate.split('T')[0] : ''}
                          onChange={(e) => {
                            const newAvail = [...(formData.availability || [])];
                            newAvail[i].endDate = new Date(e.target.value).toISOString();
                            setFormData(prev => ({ ...prev, availability: newAvail }));
                          }}
                          className="w-full border-none p-0 text-xs dark:text-white bg-transparent focus:ring-0"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500">Spots</label>
                        <input
                          type="number"
                          value={avail.availableSpots}
                          onChange={(e) => {
                            const newAvail = [...(formData.availability || [])];
                            newAvail[i].availableSpots = parseInt(e.target.value) || 0;
                            setFormData(prev => ({ ...prev, availability: newAvail }));
                          }}
                          className="w-full border-none p-0 text-xs dark:text-white bg-transparent focus:ring-0"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                         <div>
                          <label className="block text-[10px] uppercase font-bold text-gray-500">Status</label>
                          <select
                            value={avail.status}
                            onChange={(e) => {
                              const newAvail = [...(formData.availability || [])];
                              newAvail[i].status = e.target.value as any;
                              setFormData(prev => ({ ...prev, availability: newAvail }));
                            }}
                            className="w-full border-none p-0 text-[10px] dark:text-white bg-transparent focus:ring-0 font-bold"
                          >
                            <option value="Available">Available</option>
                            <option value="Limited">Limited</option>
                            <option value="Sold Out">Sold Out</option>
                          </select>
                         </div>
                         <button type="button" onClick={() => {
                            const newAvail = (formData.availability || []).filter((_, idx) => idx !== i);
                            setFormData(prev => ({ ...prev, availability: newAvail }));
                         }} className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                            <TrashIcon className="h-4 w-4" />
                         </button>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    const newAvailItem = {
                      id: `avail-${Date.now()}`,
                      startDate: new Date().toISOString(),
                      endDate: new Date().toISOString(),
                      availableSpots: formData.maxParticipants,
                      price: formData.price,
                      status: 'Available' as const
                    };
                    setFormData(prev => ({ ...prev, availability: [...(prev.availability || []), newAvailItem] }));
                  }} className="w-full py-3 border-2 border-dashed border-emerald-200 dark:border-emerald-500/20 text-emerald-600 rounded-xl text-xs font-bold hover:bg-emerald-500/5 transition-all flex items-center justify-center gap-2">
                    <PlusIcon className="h-4 w-4" /> Add Specific Tour Dates
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Policy */}
            <div className="mt-12 border-t dark:border-white/10 pt-8 mb-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Professional Payment Policy</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                Set industry-standard deposit requirements and enrollment flexibility.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-emerald-50/30 dark:bg-emerald-500/5 p-6 rounded-2xl border border-emerald-100 dark:border-emerald-500/10">
                <div>
                  <label className="block text-sm font-bold text-emerald-900 dark:text-emerald-400 mb-2">Default Deposit (%)</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="depositPercentage"
                      value={formData.depositPercentage}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-500/20 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white"
                      placeholder="e.g. 30"
                    />
                    <span className="absolute right-4 top-3.5 text-emerald-500 font-bold">%</span>
                  </div>
                  <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/50 mt-2 leading-tight">
                    Minimum commitment required to confirm a booking and secure lodge availability.
                  </p>
                </div>

                <div className="flex flex-col justify-center">
                  <label className="flex items-center gap-4 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={formData.allowInstallments}
                        onChange={(e) => setFormData(prev => ({ ...prev, allowInstallments: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </div>
                    <div>
                      <span className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-emerald-500 transition-colors block">
                        Installment Plans
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 block mt-0.5">
                        Allow clients to spread payments after the initial deposit.
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            {/* Safari Guides */}
            <div className="mt-8 border-t pt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Safari Guides</h3>
              <p className="text-sm text-gray-500 mb-4">
                Assign one or more expert guides to this tour. If none are assigned, the tour will be marked as "Not Guided".
              </p>

              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search and add guides..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={guideSearchTerm}
                    onChange={(e) => setGuideSearchTerm(e.target.value)}
                  />
                  {guideSearchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {guides
                        .filter(g =>
                          !formData.guides.some(sg => sg.id === g.id) &&
                          ((g.firstName || '').toLowerCase().includes(guideSearchTerm.toLowerCase()) ||
                            (g.lastName || '').toLowerCase().includes(guideSearchTerm.toLowerCase()) ||
                            (g.name || '').toLowerCase().includes(guideSearchTerm.toLowerCase()))
                        )
                        .map(guide => (
                          <div
                            key={guide.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                guides: [...prev.guides, guide]
                              }));
                              setGuideSearchTerm('');
                            }}
                          >
                            <div className="w-6 h-6 rounded-full bg-gray-200 overflow-hidden">
                              {guide.avatar ? <img src={guide.avatar} className="w-full h-full object-cover" alt="" /> : <SparklesIcon className="h-4 w-4" />}
                            </div>
                            <span className="text-sm">{(guide.firstName || guide.lastName) ? `${guide.firstName} ${guide.lastName}` : (guide.name || 'Unnamed Guide')}</span>
                          </div>
                        ))}
                      {guides.filter(g =>
                        !formData.guides.some(sg => sg.id === g.id) &&
                        ((g.firstName || '').toLowerCase().includes(guideSearchTerm.toLowerCase()) ||
                          (g.lastName || '').toLowerCase().includes(guideSearchTerm.toLowerCase()) ||
                          (g.name || '').toLowerCase().includes(guideSearchTerm.toLowerCase()))
                      ).length === 0 && (
                          <div className="px-4 py-2 text-sm text-gray-500">No matching guides found</div>
                        )}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  {formData.guides.length > 0 ? (
                    formData.guides.map(guide => (
                      <div key={guide.id} className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg border border-blue-100">
                        <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center overflow-hidden">
                          {guide.avatar ? <img src={guide.avatar} className="w-full h-full object-cover" alt="" /> : <SparklesIcon className="h-4 w-4" />}
                        </div>
                        <span className="text-sm font-medium">{(guide.firstName || guide.lastName) ? `${guide.firstName} ${guide.lastName}` : (guide.name || 'Unnamed Guide')}</span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            guides: prev.guides.filter(g => g.id !== guide.id)
                          }))}
                          className="text-blue-400 hover:text-blue-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-amber-600 italic bg-amber-50 px-4 py-2 rounded-md border border-amber-100 w-full">
                      "This tour is currently not Guided. It will be professionally stated on the package page."
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-gray-50 dark:bg-slate-800/30 p-6 rounded-2xl border border-gray-100 dark:border-white/5">
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-emerald-500" />
                Visual Gallery
              </label>
              <div className="space-y-6">
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={`image-${index}-${image.slice(-20)}`} className="relative group aspect-square">
                        <img
                          src={image}
                          alt={`Tour ${index + 1}`}
                          className="w-full h-full object-cover rounded-xl border-2 border-transparent group-hover:border-emerald-500 transition-all shadow-md"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            const imageUrl = formData.images[index];
                            if (imageUrl.includes('res.cloudinary.com')) {
                               try {
                                 await fetch(`${API_BASE_URL}/upload/delete`, {
                                    method: 'DELETE',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ url: imageUrl })
                                 });
                               } catch (err) {
                                 console.error("Failed to delete from Cloudinary:", err);
                               }
                            }
                            const newImages = formData.images.filter((_, i) => i !== index);
                            setFormData(prev => ({ ...prev, images: newImages }));
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-col gap-4">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="Paste image URL here..."
                      value={newImage}
                      onChange={(e) => setNewImage(e.target.value)}
                      className="flex-1 px-4 py-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white transition-all shadow-inner"
                    />
                    <button
                      type="button"
                      onClick={handleAddImage}
                      className="px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-500 transition-all shadow-md active:scale-95"
                    >
                      Add URL
                    </button>
                  </div>
                  
                  <div className="relative flex items-center gap-3">
                    <div className="flex-1 h-[1px] bg-gray-200 dark:bg-slate-700"></div>
                    <span className="text-xs uppercase font-bold text-gray-400">OR UPLOAD</span>
                    <div className="flex-1 h-[1px] bg-gray-200 dark:bg-slate-700"></div>
                  </div>

                  <div className="flex gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                      className="hidden"
                      id="tour-image-upload"
                    />
                    <label
                      htmlFor="tour-image-upload"
                      className={`w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                        uploadingImage ? 'border-gray-300 bg-gray-100 text-gray-500' : 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30 dark:hover:bg-emerald-500/20'
                      }`}
                    >
                      {uploadingImage ? (
                        <>
                          <div className="w-5 h-5 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin"></div>
                          <span className="font-bold">Optimizing & Uploading...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          <span className="font-bold text-sm">Upload High-Quality Image from Device</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
                {errors.images && <p className="text-red-500 text-xs font-medium">{errors.images}</p>}
              </div>
            </div>

            {/* Itinerary */}
            <div className="mt-12 border-t dark:border-white/10 pt-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Expedition Itinerary</h3>
              <div className="space-y-6">
                {formData.itinerary.map((item, index) => (
                  <div key={`itinerary-${index}-${item.day}`} className="group relative bg-gray-50 dark:bg-slate-800/40 p-6 rounded-2xl border border-gray-200 dark:border-slate-700/50 transition-all hover:shadow-lg hover:border-emerald-500/30">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 flex items-center justify-center bg-emerald-500 text-white rounded-full font-black text-xs shadow-md shadow-emerald-500/20">DAY {item.day}</span>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => {
                            const newItinerary = [...formData.itinerary];
                            newItinerary[index].title = e.target.value;
                            setFormData(prev => ({ ...prev, itinerary: newItinerary }));
                          }}
                          className="bg-transparent border-none focus:ring-0 p-0 text-lg font-bold text-gray-900 dark:text-white placeholder:text-gray-400"
                          placeholder="What happens on this day?"
                        />
                      </div>
                      <button type="button" onClick={() => {
                        const newItinerary = formData.itinerary.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, itinerary: newItinerary }));
                      }} className="text-gray-400 hover:text-red-500 transition-colors p-2">
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <textarea
                      value={item.description}
                      onChange={(e) => {
                        const newItinerary = [...formData.itinerary];
                        newItinerary[index].description = e.target.value;
                        setFormData(prev => ({ ...prev, itinerary: newItinerary }));
                      }}
                      className="w-full bg-white dark:bg-slate-900/50 border border-gray-100 dark:border-slate-700 rounded-xl p-4 text-sm text-gray-600 dark:text-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none min-h-[100px] mb-4"
                      placeholder="Detailed description of the day's journey..."
                    />

                    {/* Day Activities */}
                    <div className="mb-4">
                      <label className="block text-[10px] uppercase font-black text-gray-400 mb-2">Activities for Day {item.day}</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(item.activities || []).map((act, actIdx) => (
                          <div key={actIdx} className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs border border-emerald-200">
                            <span>{act}</span>
                            <button type="button" onClick={() => {
                              const newItin = [...formData.itinerary];
                              newItin[index].activities = (newItin[index].activities || []).filter((_, i) => i !== actIdx);
                              setFormData(prev => ({ ...prev, itinerary: newItin }));
                            }}>
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Add activity..."
                          className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-xs"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = e.currentTarget.value.trim();
                              if (val) {
                                const newItin = [...formData.itinerary];
                                newItin[index].activities = [...(newItin[index].activities || []), val];
                                setFormData(prev => ({ ...prev, itinerary: newItin }));
                                e.currentTarget.value = '';
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Day Meals */}
                    <div>
                      <label className="block text-[10px] uppercase font-black text-gray-400 mb-2">Meals Included</label>
                      <div className="flex gap-4">
                        {['Breakfast', 'Lunch', 'Dinner'].map(meal => (
                          <label key={meal} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(item.meals || []).includes(meal)}
                              onChange={(e) => {
                                const newItin = [...formData.itinerary];
                                const currentMeals = newItin[index].meals || [];
                                if (e.target.checked) {
                                  newItin[index].meals = [...currentMeals, meal];
                                } else {
                                  newItin[index].meals = currentMeals.filter(m => m !== meal);
                                }
                                setFormData(prev => ({ ...prev, itinerary: newItin }));
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-xs text-gray-600 dark:text-slate-400 font-medium">{meal}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Day Accommodation */}
                    <div className="mt-4 border-t border-gray-100 dark:border-slate-700/50 pt-4">
                      <label className="block text-[10px] uppercase font-black text-gray-400 mb-2">Overnight Accommodation</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <select
                          value={item.accommodation?.providerId || (typeof item.accommodation === 'string' ? '' : '')}
                          onChange={(e) => {
                            const newItin = [...formData.itinerary];
                            const providerId = e.target.value;
                            const provider = accommodations.find(a => a.id === providerId);
                            if (providerId) {
                                newItin[index].accommodation = {
                                    providerId,
                                    name: provider?.name || '',
                                    tier: item.accommodation?.tier || 'Full Board'
                                };
                            } else {
                                newItin[index].accommodation = undefined;
                            }
                            setFormData(prev => ({ ...prev, itinerary: newItin }));
                          }}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-sm"
                        >
                          <option value="">No accommodation selected</option>
                          {loadingAccommodations ? (
                             <option disabled>Loading accommodations...</option>
                          ) : (
                             accommodations.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.name} ({acc.type})</option>
                             ))
                          )}
                        </select>

                        {item.accommodation?.providerId && (
                          <select
                            value={item.accommodation.tier || 'Full Board'}
                            onChange={(e) => {
                               const newItin = [...formData.itinerary];
                               if (newItin[index].accommodation && typeof newItin[index].accommodation !== 'string') {
                                   newItin[index].accommodation.tier = e.target.value;
                               }
                               setFormData(prev => ({ ...prev, itinerary: newItin }));
                            }}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-sm"
                          >
                            <option value="Full Board">Full Board (All meals)</option>
                            <option value="Half Board">Half Board</option>
                            <option value="B&B">Bed & Breakfast</option>
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    itinerary: [...prev.itinerary, { day: prev.itinerary.length + 1, title: '', description: '', activities: [], meals: [] }]
                  }))}
                  className="w-full py-4 border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-2xl text-gray-500 dark:text-slate-400 font-bold hover:border-emerald-500 hover:text-emerald-500 transition-all flex items-center justify-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" /> Add Expedition Day
                </button>
              </div>
            </div>

            {/* Inclusions/Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 border-t dark:border-white/10 pt-8">
              <div className="bg-emerald-50/20 dark:bg-emerald-500/5 p-6 rounded-2xl border border-emerald-100 dark:border-emerald-500/10 transition-all hover:bg-emerald-50/30">
                <label className="block text-sm font-bold text-emerald-900 dark:text-emerald-400 mb-4 flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5" /> What's Included
                </label>
                <div className="space-y-3">
                  {formData.includes.map((inc, i) => (
                    <div key={`inc-${i}-${inc.slice(0, 10)}`} className="flex gap-2 group">
                      <input
                        value={inc}
                        onChange={(e) => {
                          const newInc = [...formData.includes];
                          newInc[i] = e.target.value;
                          setFormData(prev => ({ ...prev, includes: newInc }));
                        }}
                        className="flex-1 bg-white dark:bg-slate-900/50 border border-emerald-100 dark:border-emerald-500/20 rounded-xl px-4 py-2 text-sm dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                        placeholder="e.g. Professional Safari Guide"
                      />
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, includes: prev.includes.filter((_, idx) => idx !== i) }))} className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, includes: [...prev.includes, ''] }))} className="w-full py-2 rounded-xl border border-dashed border-emerald-200 dark:border-emerald-500/30 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 transition-all">+ Add Inclusion</button>
                </div>
              </div>

              <div className="bg-red-50/20 dark:bg-red-500/5 p-6 rounded-2xl border border-red-100 dark:border-red-500/10 transition-all hover:bg-red-50/30">
                <label className="block text-sm font-bold text-red-900 dark:text-red-400 mb-4 flex items-center gap-2">
                  <XMarkIcon className="h-5 w-5" /> What's Excluded
                </label>
                <div className="space-y-3">
                  {formData.excludes.map((exc, i) => (
                    <div key={`exc-${i}-${exc.slice(0, 10)}`} className="flex gap-2 group">
                      <input
                        value={exc}
                        onChange={(e) => {
                          const newExc = [...formData.excludes];
                          newExc[i] = e.target.value;
                          setFormData(prev => ({ ...prev, excludes: newExc }));
                        }}
                        className="flex-1 bg-white dark:bg-slate-900/50 border border-red-100 dark:border-red-500/20 rounded-xl px-4 py-2 text-sm dark:text-white focus:ring-2 focus:ring-red-500 outline-none"
                        placeholder="e.g. International Airfare"
                      />
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, excludes: prev.excludes.filter((_, idx) => idx !== i) }))} className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, excludes: [...prev.excludes, ''] }))} className="w-full py-2 rounded-xl border border-dashed border-red-200 dark:border-red-500/30 text-xs font-bold text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-all">+ Add Exclusion</button>
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="mt-12">
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-blue-500" /> Safari Requirements
              </label>
              <div className="bg-gray-50 dark:bg-slate-800/20 p-6 rounded-2xl border border-gray-100 dark:border-slate-700/50">
                <div className="space-y-3">
                  {formData.requirements.map((req, i) => (
                    <div key={`req-${i}-${req.slice(0, 10)}`} className="flex gap-2 group">
                      <input
                        value={req}
                        onChange={(e) => {
                          const newReq = [...formData.requirements];
                          newReq[i] = e.target.value;
                          setFormData(prev => ({ ...prev, requirements: newReq }));
                        }}
                        className="flex-1 bg-white dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                        placeholder="e.g. Valid Passport (6+ months remaining)"
                      />
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, requirements: prev.requirements.filter((_, idx) => idx !== i) }))} className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, requirements: [...prev.requirements, ''] }))} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-500 dark:text-slate-400 hover:border-emerald-500 hover:text-emerald-500 transition-all">
                    + Add Requirement
                  </button>
                </div>
              </div>
            </div>


            {/* Tiered Pricing Editor */}
            <div className="mt-16 border-t dark:border-white/10 pt-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Tier Pricing & Logistics</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 font-medium">
                Configure specific pricing, vehicle types, and inclusions for each tier (Budget, Standard, Luxury).
              </p>

              <div className="bg-white/50 dark:bg-slate-900/50 rounded-2xl p-6 ring-1 ring-black/5 dark:ring-white/10 shadow-xl backdrop-blur-sm">
                <TierEditor
                  variants={formData.tierVariants || {}}
                  onChange={(updatedVariants) => setFormData(prev => ({ ...prev, tierVariants: updatedVariants }))}
                />
              </div>

              {/* Discount Section */}
              <div className="mt-8 pt-8 border-t dark:border-white/5">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Discounted (NOW) Pricing</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Set lower prices to show as "NOW" prices with original prices crossed out.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.discountedTierVariants) {
                        setFormData(prev => ({ ...prev, discountedTierVariants: undefined }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          discountedTierVariants: JSON.parse(JSON.stringify(prev.tierVariants || {}))
                        }));
                      }
                    }}
                    className={`px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-md ${formData.discountedTierVariants
                      ? 'bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20'
                      : 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 hover:bg-emerald-500/20'
                      }`}
                  >
                    {formData.discountedTierVariants ? 'Remove Discount' : 'Add Discount Plan'}
                  </button>
                </div>

                {formData.discountedTierVariants && (
                  <div className="bg-emerald-500/5 p-8 rounded-2xl border border-emerald-500/20 animate-in fade-in slide-in-from-top-4 duration-500 shadow-lg">
                    <div className="flex items-center gap-3 text-emerald-500 mb-6 font-black text-xs uppercase tracking-widest">
                      <SparklesIcon className="h-5 w-5" />
                      <span>Promotional Rates Active</span>
                    </div>
                    <TierEditor
                      variants={formData.discountedTierVariants}
                      onChange={(updatedVariants) => setFormData(prev => ({ ...prev, discountedTierVariants: updatedVariants }))}
                    />
                  </div>
                )}
              </div>
            </div>


            {/* Seasonal Adjustments */}
            <div className="mt-16 border-t dark:border-white/10 pt-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Seasonal Adjustments</h3>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 mb-8">
                <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2 font-medium">
                  <InformationCircleIcon className="h-5 w-5" />
                  Define custom seasonal multipliers for this specific package. These will override global default seasons.
                </p>
              </div>

              <div className="space-y-4">
                {formData.seasonalAdjustments?.map((season, index) => (
                  <div key={season.id || `season-${index}`} className="bg-gray-50 dark:bg-slate-800/30 p-6 rounded-2xl border border-gray-100 dark:border-slate-700/50 relative group transition-all hover:bg-white dark:hover:bg-slate-800/50 shadow-sm">
                    <button
                      type="button"
                      onClick={() => {
                        const newSeasons = [...(formData.seasonalAdjustments || [])];
                        newSeasons.splice(index, 1);
                        setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                      }}
                      className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-2 shadow-xl opacity-0 group-hover:opacity-100 hover:scale-110 transition-all z-10"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                      <div className="md:col-span-4">
                        <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase mb-2 tracking-widest">Season Name</label>
                        <input
                          type="text"
                          value={season.name}
                          onChange={(e) => {
                            const newSeasons = [...(formData.seasonalAdjustments || [])];
                            newSeasons[index] = { ...season, name: e.target.value as any };
                            setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                          }}
                          className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                          placeholder="e.g. Migration Peak"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase mb-2 tracking-widest">Multiplier</label>
                        <input
                          type="number"
                          step="0.1"
                          value={season.priceMultiplier}
                          onChange={(e) => {
                            const newSeasons = [...(formData.seasonalAdjustments || [])];
                            newSeasons[index] = { ...season, priceMultiplier: parseFloat(e.target.value) || 1.0 };
                            setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                          }}
                          className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl dark:text-white text-center font-bold"
                          placeholder="1.5"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase mb-2 tracking-widest">Start Date</label>
                        <div className="flex gap-2">
                          <select
                            value={season.startMonth}
                            onChange={(e) => {
                              const newSeasons = [...(formData.seasonalAdjustments || [])];
                              newSeasons[index] = { ...season, startMonth: parseInt(e.target.value) };
                              setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                            }}
                            className="flex-1 px-2 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-xs dark:text-white appearance-none"
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'short' })}</option>
                            ))}
                          </select>
                          <select
                            value={season.startDay || 1}
                            onChange={(e) => {
                              const newSeasons = [...(formData.seasonalAdjustments || [])];
                              newSeasons[index] = { ...season, startDay: parseInt(e.target.value) };
                              setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                            }}
                            className="w-16 px-2 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-xs dark:text-white appearance-none"
                          >
                            {Array.from({ length: 31 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase mb-2 tracking-widest">End Date</label>
                        <div className="flex gap-2">
                          <select
                            value={season.endMonth}
                            onChange={(e) => {
                              const newSeasons = [...(formData.seasonalAdjustments || [])];
                              newSeasons[index] = { ...season, endMonth: parseInt(e.target.value) };
                              setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                            }}
                            className="flex-1 px-2 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-xs dark:text-white appearance-none"
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'short' })}</option>
                            ))}
                          </select>
                          <select
                            value={season.endDay || 28}
                            onChange={(e) => {
                              const newSeasons = [...(formData.seasonalAdjustments || [])];
                              newSeasons[index] = { ...season, endDay: parseInt(e.target.value) };
                              setFormData(prev => ({ ...prev, seasonalAdjustments: newSeasons }));
                            }}
                            className="w-16 px-2 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-xs dark:text-white appearance-none"
                          >
                            {Array.from({ length: 31 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newSeason = {
                      id: `season-${Date.now()}`,
                      name: 'High' as const,
                      startMonth: 1,
                      startDay: 1,
                      endMonth: 12,
                      endDay: 31,
                      priceMultiplier: 1.0
                    };
                    setFormData(prev => ({
                      ...prev,
                      seasonalAdjustments: [...(prev.seasonalAdjustments || []), newSeason]
                    }));
                  }}
                  className="w-full py-4 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-2xl text-gray-500 dark:text-slate-400 font-bold hover:border-emerald-500 hover:text-emerald-500 transition-all flex items-center justify-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" /> Add Seasonal Override
                </button>
              </div>
            </div>

            {/* Safari Extras */}
            <div className="mt-16 border-t dark:border-white/10 pt-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Safari Add-Ons</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 font-medium">
                Add optional experiences or services that clients can add to their expedition.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formData.extras?.map((extra, index) => (
                  <div key={extra.id || `extra-${index}`} className="bg-blue-50/10 dark:bg-blue-500/5 p-6 rounded-2xl border border-blue-100 dark:border-blue-500/20 relative group transition-all hover:bg-blue-50/20 shadow-sm">
                    <button
                      type="button"
                      onClick={() => {
                        const newExtras = [...(formData.extras || [])];
                        newExtras.splice(index, 1);
                        setFormData(prev => ({ ...prev, extras: newExtras }));
                      }}
                      className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-2 shadow-xl opacity-0 group-hover:opacity-100 hover:scale-110 transition-all z-10"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>

                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <label className="block text-[10px] font-black text-blue-400 uppercase mb-2 tracking-widest">Icon</label>
                        <input
                          type="text"
                          value={extra.icon || ''}
                          onChange={(e) => {
                            const newExtras = [...(formData.extras || [])];
                            newExtras[index] = { ...extra, icon: e.target.value };
                            setFormData(prev => ({ ...prev, extras: newExtras }));
                          }}
                          className="w-full px-2 py-2.5 bg-white dark:bg-slate-900 border border-blue-100 dark:border-blue-900/10 rounded-xl text-center text-lg"
                          placeholder="🎈"
                        />
                      </div>
                      <div className="col-span-9">
                        <label className="block text-[10px] font-black text-blue-400 uppercase mb-2 tracking-widest">Full Name</label>
                        <input
                          type="text"
                          value={extra.name}
                          onChange={(e) => {
                            const newExtras = [...(formData.extras || [])];
                            newExtras[index] = { ...extra, name: e.target.value };
                            setFormData(prev => ({ ...prev, extras: newExtras }));
                          }}
                          className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-blue-100 dark:border-blue-900/10 rounded-xl dark:text-white font-bold"
                          placeholder="e.g. Hot Air Balloon Safari"
                        />
                      </div>
                      <div className="col-span-6">
                        <label className="block text-[10px] font-black text-blue-400 uppercase mb-2 tracking-widest">Price (USD)</label>
                        <input
                          type="number"
                          value={extra.price}
                          onChange={(e) => {
                            const newExtras = [...(formData.extras || [])];
                            newExtras[index] = { ...extra, price: parseFloat(e.target.value) || 0 };
                            setFormData(prev => ({ ...prev, extras: newExtras }));
                          }}
                          className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-blue-100 dark:border-blue-900/10 rounded-xl dark:text-white font-black"
                          placeholder="450"
                        />
                      </div>
                      <div className="col-span-6">
                        <label className="block text-[10px] font-black text-blue-400 uppercase mb-2 tracking-widest">Unit</label>
                        <select
                          value={extra.unit}
                          onChange={(e) => {
                            const newExtras = [...(formData.extras || [])];
                            newExtras[index] = { ...extra, unit: e.target.value as any };
                            setFormData(prev => ({ ...prev, extras: newExtras }));
                          }}
                          className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-blue-100 dark:border-blue-900/10 rounded-xl dark:text-white text-sm"
                        >
                          <option value="per person">per person</option>
                          <option value="per group">per group</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  const newExtra = {
                    id: `extra-${Date.now()}`,
                    name: '',
                    price: 0,
                    unit: 'per person' as const,
                    icon: '✨'
                  };
                  setFormData(prev => ({
                    ...prev,
                    extras: [...(prev.extras || []), newExtra]
                  }));
                }}
                className="w-full mt-6 py-4 border-2 border-dashed border-blue-200 dark:border-blue-900/30 rounded-2xl text-blue-500 font-bold hover:bg-blue-500/10 transition-all flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-5 w-5" /> Add Safari Experience/Extra
              </button>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-6 mt-20 border-t dark:border-white/10 pt-8 sticky bottom-0 bg-white/5 dark:bg-transparent backdrop-blur-xl py-4 z-40">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 text-sm font-bold text-gray-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 transition-all"
              >
                Discard Changes
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-10 py-3 bg-sky-500 hover:bg-sky-600 text-white text-sm font-black rounded-xl shadow-xl shadow-sky-500/20 transition-all active:scale-95 transform-gpu disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  tour ? 'Save Expedition Changes' : 'Launch New Safari Package'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TourForm;